import React from "react";

import "./TopPlayersPage.css";

import { useDispatch, useSelector } from "react-redux";

import { topPlayersReducer } from "./reducer";
import { selectLoading, selectTopPlayers } from "./selectors";
import Flex from "../../components/Flex/Flex";
import Table from "../../components/Table/Table";
import Text from "../../components/Text/Text";
import TopPlayerDto from "../../dto/TopPlayerDto";
import formatWinnings from "../../utils/formatWinnings";
import {
  getPokersiteNameByPokersiteId,
  pokerSitePostfix,
} from "../StatPage/utils";
import { useAuth } from "../../hooks/useAuth";
import TableSizeFilter from "../../components/TableSizeFilter/TableSizeFilter";
import LimitFilter from "../../components/LimitFilter/LimitsFilter";
import DateFilter, {
  CustomDateFilterValue,
} from "../../components/DateFilter/DateFilter";
import Button from "../../components/Button/Button";
import { findStats } from "../StatPage/actions";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TopPlayersPage = React.memo(() => {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  useAuth();

  const [tableSizeFilterValue, setTableSizeFilterValue] = React.useState<
    number | undefined
  >();

  const [limitFilterValue, setLimitFilterValue] = React.useState<number>(0);

  const [dateFilterValue, setDateFilterValue] = React.useState<
    number | undefined
  >();

  const [customDateFilterValue, setCustomDateFilterValue] = React.useState<
    CustomDateFilterValue | undefined
  >();

  const loading = useSelector(selectLoading);
  const topPlayers = useSelector(selectTopPlayers);

  React.useEffect(() => {
    dispatch(
      topPlayersReducer.actions.loadTopPlayers({
        limitFilterValue: 0,
      })
    );
  }, [dispatch]);

  const handleChangeTableSizeFilter = React.useCallback((value?: number) => {
    setTableSizeFilterValue(value);
  }, []);

  const handleChangeLimitFilter = React.useCallback((value: number) => {
    setLimitFilterValue(value);
  }, []);

  const handleDateFilter = React.useCallback(
    (value?: number, customValue?: CustomDateFilterValue) => {
      setDateFilterValue(value);
      setCustomDateFilterValue(customValue);
    },
    []
  );

  const handleApply = React.useCallback(() => {
    dispatch(
      topPlayersReducer.actions.loadTopPlayers({
        tableSizeFilterValue,
        limitFilterValue,
        dateFilterValue,
        customDateFilterValue,
      })
    );
  }, [
    dispatch,
    tableSizeFilterValue,
    limitFilterValue,
    dateFilterValue,
    customDateFilterValue,
  ]);

  const handleReset = React.useCallback(() => {
    setTableSizeFilterValue(undefined);
    setLimitFilterValue(0);
    setDateFilterValue(undefined);
    setCustomDateFilterValue(undefined);
  }, []);

  const handleFindStats = React.useCallback(
    (player: TopPlayerDto) => {
      dispatch(
        findStats({
          playername: player.playername,
          pokersiteId: player.pokersite_id,
        })
      );
    },
    [dispatch]
  );

  if (loading) {
    return null;
  }

  return (
    <Flex
      className="TopPlayersPage"
      justify="center"
      vertical
      gap={32}
    >
      <Flex
        gap={32}
        wrap
      >
        <TableSizeFilter
          value={tableSizeFilterValue}
          onChange={handleChangeTableSizeFilter}
        />

        <LimitFilter
          value={limitFilterValue}
          onChange={handleChangeLimitFilter}
        />
        <DateFilter
          value={dateFilterValue}
          customValue={customDateFilterValue}
          onChange={handleDateFilter}
        />
      </Flex>
      <Flex
        justify="flex-end"
        gap={16}
      >
        <Button onClick={handleReset}>{t("reset")}</Button>
        <Button
          type="primary"
          onClick={handleApply}
        >
          {t("apply")}
        </Button>
      </Flex>
      <Table
        columns={[
          {
            title: t("rating"),
            dataIndex: "rating",
            render: (_: any, index: number) => <Text>#{index + 1}</Text>,
          },
          {
            title: t("player"),
            dataIndex: "playername",
            render: (player: TopPlayerDto) => (
              <Link
                className="TopPlayersPage__Playername"
                to={`/statistics?playername=${player.playername}&room=${getPokersiteNameByPokersiteId(player.pokersite_id)}`}
                onClick={() => handleFindStats(player)}
              >
                {player.playername} ({pokerSitePostfix[player.pokersite_id]})
              </Link>
            ),
          },
          {
            title: "Hands",
            dataIndex: "hands",
          },
          {
            title: "Winnings",
            dataIndex: "totalamountwonincents",
            render: (player: TopPlayerDto) => (
              <Text type={player.winnings > 0 ? "success" : "error"}>
                {formatWinnings(player.winnings, 2, false)}
              </Text>
            ),
          },
          { title: "bb/100", dataIndex: "bb100" },
        ]}
        data={topPlayers}
        pagination={false}
      />
    </Flex>
  );
});

export default TopPlayersPage;
